/**
 * Life4Earth V2 — React hook for the effective role-definition view
 * scoped to a specific entity.
 *
 * DEV-1186 TU-2 Pattern B / DEV-1187. Wraps the
 * GET /api/roledefinitions/effective endpoint, which returns role
 * definitions with their per-entity overrides COALESCEd in. This is
 * the canonical "what roles can I assign on this entity?" query —
 * used by slot-creation pickers, the unified TeamTab catalog view
 * (TU-7), and any other consumer that needs the effective shape.
 *
 * Phase 1 scoping: exact-entity match (override applies only when
 * caller's owningEntityGuid equals the override's owningEntityGuid).
 *
 * Passing a nullish owningEntityGuid is the "idle" state — the hook
 * returns empty result without firing a fetch. Safe to mount
 * unconditionally and let the caller pass null when no relevant
 * entity is selected.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchEffectiveRoleDefinitions,
  type EffectiveRoleDefinitionResponse,
} from "./roleDefinitionOverrides";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiEffectiveRoleDefinitionsResult {
  definitions: EffectiveRoleDefinitionResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading" | "idle";
  refetch: () => void;
}

function cacheKeyFor(owningEntityGuid: string, pillarType?: string): string {
  const pt = pillarType ?? "*";
  return `roleDef:effective:${owningEntityGuid.toLowerCase()}:${pt}`;
}

export function useApiEffectiveRoleDefinitions(
  owningEntityGuid: string | null | undefined,
  pillarType?: string,
): UseApiEffectiveRoleDefinitionsResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiEffectiveRoleDefinitionsResult>({
    definitions: [],
    loading: !!owningEntityGuid,
    error: null,
    source: owningEntityGuid ? "loading" : "idle",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    if (!owningEntityGuid) {
      setData({
        definitions: [],
        loading: false,
        error: null,
        source: "idle",
        refetch,
      });
      return;
    }
    try {
      const definitions = await fetchForKey<EffectiveRoleDefinitionResponse[]>(
        cacheKeyFor(owningEntityGuid, pillarType),
        () => fetchEffectiveRoleDefinitions(owningEntityGuid, pillarType),
        { force: true },
      );
      if (!mountedRef.current) return;
      setData({
        definitions: definitions ?? [],
        loading: false,
        error: null,
        source: (definitions && definitions.length > 0) ? "api" : "empty",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        definitions: [],
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        source: "empty",
        refetch,
      }));
    }
  }, [owningEntityGuid, pillarType]);

  useEffect(() => {
    mountedRef.current = true;
    void refetch();

    const onCacheUpdate = (e: Event) => {
      if (!owningEntityGuid) return;
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === cacheKeyFor(owningEntityGuid, pillarType)) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [refetch, owningEntityGuid, pillarType]);

  return data;
}
